
import React, { useState, useEffect } from 'react';
import { Coins, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '@/hooks/use-toast';

interface WeeklyBalanceProps {
  className?: string;
}

export const WeeklyBalance: React.FC<WeeklyBalanceProps> = ({ className }) => {
  const [balance, setBalance] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>('0');
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchBalance();
    
    // Set up real-time listener for balance updates
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'weekly_balances'
        },
        (payload) => {
          if (payload.new) {
            setBalance((payload.new as any).amount || 0);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchBalance = async () => {
    try {
      const { data, error } = await supabase
        .from('weekly_balances')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setBalance(data.amount);
        setEditValue(data.amount.toString());
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const saveBalance = async () => {
    try {
      const numValue = Number(editValue);
      
      if (isNaN(numValue)) {
        toast({
          title: "Invalid value",
          description: "Please enter a valid number",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('weekly_balances')
        .insert([{ amount: numValue }]);

      if (error) throw error;

      setBalance(numValue);
      setIsEditing(false);
      
      toast({
        title: "Balance updated",
        description: "Weekly balance has been updated successfully",
      });
    } catch (error) {
      console.error('Error saving balance:', error);
      toast({
        title: "Update failed",
        description: "There was an error updating the balance",
        variant: "destructive",
      });
    }
  };

  const handleEdit = () => {
    setEditValue(balance.toString());
    setIsEditing(true);
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex items-center gap-2 text-amber-500">
        <Coins className="h-5 w-5" />
        {isEditing && isAdmin ? (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-24 h-8 bg-[#1e293b] text-white"
            />
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={saveBalance}
              className="h-8 px-2 text-green-500 hover:text-green-400 hover:bg-[#1e293b]"
            >
              Save
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsEditing(false)}
              className="h-8 px-2 text-gray-500 hover:text-gray-400 hover:bg-[#1e293b]"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-medium">Weekly Balance: {balance}</span>
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleEdit}
                className="h-6 w-6 text-gray-400 hover:text-white"
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
